import React, { Suspense, lazy } from 'react';
// eslint-disable-next-line no-restricted-imports -- TODO: legacy connect (baseline)
import { connect } from 'react-redux';
import { Route, Redirect, Switch } from 'react-router-dom';

import PrivateRoute from '../components/router/private-route';
import ScrollToTop from '../components/router/top-scroll';
import { roles, injectRoleManagement } from '../utils/permissions/role-utils';
import { NotFound } from '../components/error/error';
import FallbackPage from '../components/fallback-page/fallback-page';

import App from '../containers/app/index';
import UserContainer from '../containers/user';
import PageContainer from '../containers/page-container/index';
import { REPORT, productToPath } from '../containers/product-initializer/products';
import ProductInitializer from '../containers/product-initializer/product-initializer';
import WaiterOfProcess from '../components/waiter-of-process/waiter-of-process';
import LocaleLoader from '../containers/locale-loader';
import SettingsWrapper from '../containers/setting-wrapper';

import { scoutAdminIndex, clientsAdminIndex, scheduleAdminIndex } from '../constants/page-names';

import LicensePage from '../pages/license/license-page';
import ResetPassword from '../pages/auth/reset-password';
import { AsyncPostGameReportsPage } from '../pages/async-pages';
import Login from '../pages/auth/login';
import SignUp from '../pages/auth/sign-up';
import ForgotPassword from '../pages/auth/forgot-password';
import RequestUnlock from '../pages/auth/request-unlock';
import UnblockAccount from '../pages/auth/unblock-account';
import EmailConfirmation from '../pages/email-confirmation/email-confirmation';
import SsoLogin from '../pages/auth/sso-login';
import ProgressIndicator from '../pages/progress-indicator/progress-indicator';
import MountNotifier from '../pages/progress-indicator/mount-notifier';
import MediaPlayerRoute from '../pages/media-player/media-player-lazy-route';
import ReportPurchaseReport from '../pages/report-purchase-report/purchase-page';
import ReportPurchaseStatus from '../pages/report-purchase-report/purchase-status';
import AuthByToken from '../pages/auth-by-token/auth-by-token';
import { SIGN_UP_IS_AVAILABLE } from '../pages/auth/auth-option-links';
import EmailUpdateByToken from '../pages/email-update-by-token';
import PasswordUpdate from '../pages/auth/password-update';

import { isNahlPlayerAccount, isProspectMode } from '../selectors/user/user-features-selector';

import './workaround-css-imports';
import Disabled from './disabled';
import { GlobalStyles, MeetingProvider, lightTheme } from 'amazon-chime-sdk-component-library-react';
// eslint-disable-next-line no-restricted-imports
import { ThemeProvider } from 'styled-components';
import DmcaPage from 'pages/dmca/dmca-page';
import LicenseRequiredRoute from '../pages/license/license-required-route';
import AuthTokenWrapper from 'containers/auth-token-wrapper';

// The report pages (and the video player of their widgets) are loaded on their routes
const PlayerReport = lazy(() => import(/* webpackChunkName: "report-pages" */ '../pages/report/player-report'));
const GoalieReport = lazy(() => import(/* webpackChunkName: "report-pages" */ '../pages/report/goalie-report'));
const TeamNewReport = lazy(() => import(/* webpackChunkName: "report-pages" */ '../pages/report/team-new-report'));

const AdminRoutes = lazy(() => import(/* webpackChunkName: "admin-pages" */ '../pages/admin/admin'));
const ClientRoutes = lazy(() => import(/* webpackChunkName: "client-pages" */ '../pages/client/client-routes'));
const NahlPlayerRoutes = lazy(() =>
    import(/* webpackChunkName: "player-pages" */ '../pages/nahl-player/nahl-player-routes')
);
const CollectorRoutes = lazy(() => import(/* webpackChunkName: "collector-pages" */ '../pages/collector/collector'));

//object returned by `lazy` is not valid React-Router component
// eslint-disable-next-line tpe/no-props-spread -- TODO: legacy props spread (baseline)
const AsyncAdminRoutes = props => <AdminRoutes {...props} />;
// eslint-disable-next-line tpe/no-props-spread -- TODO: legacy props spread (baseline)
const AsyncCollectorRoutes = props => <CollectorRoutes {...props} />;

//todo: export page names from constants/page-names
const RootRedirect = injectRoleManagement(({ hasAnyRole, hasProduct }) => {
    if (hasAnyRole([roles.ADMIN, roles.SCOUT_ADMIN])) {
        return <Redirect to={`/${clientsAdminIndex.root}/${clientsAdminIndex.users}`} />;
    }
    if (hasAnyRole([roles.CLIENT, roles.ANALYST])) {
        for (let key in productToPath) {
            if (hasProduct(key)) {
                return <Redirect to={`${productToPath[key].path}/home`} />;
            }
        }

        return <NotFound />;
    }

    // DEPRECATED (NFT): NFT-only users get NotFound, the NFT pages are gone. TODO: remove with the NFT roles.
    if (hasAnyRole([roles.NFT_DESIGNER, roles.NFT_MODERATOR, roles.NFT_VERIFIER, roles.NFT_MANAGER])) {
        // return <Redirect to={`/nft/home`} />;
        return <NotFound />;
    }

    return <Redirect to={`/${scheduleAdminIndex.root}/${scheduleAdminIndex.games}`} />;
});

const fallback = (
    <MountNotifier>
        <PageContainer>
            <FallbackPage />
        </PageContainer>
    </MountNotifier>
);

const Authorized = ({ nahlPlayerAccount, processing, prospectMode }) => (
    <>
        {!prospectMode && <ProgressIndicator />}
        <ThemeProvider theme={lightTheme}>
            <GlobalStyles />
            {/* The background blur provider is on the Connect page: here it downloaded the blur worker, WASM and
                model from the Chime CDN on every page (docs/migration/03-chime-sdk.md) */}
            <MeetingProvider enableWebAudio>
                <UserContainer>
                    <Suspense fallback={fallback}>
                        <Switch>
                            <Route exact path="/" component={RootRedirect} />
                            <Route
                                path={`/(${scoutAdminIndex.root}|${clientsAdminIndex.root}|${scheduleAdminIndex.root})`}
                                component={AsyncAdminRoutes}
                            />
                            <Route path="/(collector|lite-collector|jobs)" component={AsyncCollectorRoutes} />
                            {!processing && !nahlPlayerAccount && <ClientRoutes />}
                            {!processing && !!nahlPlayerAccount && <NahlPlayerRoutes />}
                        </Switch>
                    </Suspense>
                </UserContainer>
            </MeetingProvider>
        </ThemeProvider>
    </>
);

const AuthorizedWrapper = connect(state => ({
    processing: !state.user || state.user.isProcessing,
    nahlPlayerAccount: isNahlPlayerAccount(state),
    prospectMode: isProspectMode(state)
}))(Authorized);

const ReportProduct = props => (
    <ProductInitializer product={REPORT}>
        {/* eslint-disable-next-line tpe/no-props-spread -- TODO: legacy props spread (baseline) */}
        <AsyncPostGameReportsPage {...props} />
    </ProductInitializer>
);

const isDisableAll = false;

const Routes = () => {
    if (isDisableAll) {
        return <Disabled />;
    }

    return (
        <App>
            <SettingsWrapper>
                <ScrollToTop>
                    <Switch>
                        <Route path="/login" component={Login} />
                        {!!SIGN_UP_IS_AVAILABLE && <Route path="/sign-up" component={SignUp} />}
                        <Route path="/reset" component={ResetPassword} />
                        <Route path="/forgot-password" component={ForgotPassword} />
                        <Route path="/request-unlock" component={RequestUnlock} />
                        <Route path="/unblock-account" component={UnblockAccount} />
                        <Route path="/confirmation" component={EmailConfirmation} />
                        <Route path="/set-password" component={PasswordUpdate} />
                        <Route path="/update-email" component={EmailUpdateByToken} />
                        <Route path="/license" component={LicensePage} />
                        <Route path="/dmca" component={DmcaPage} />
                        <Route path="/game-reports" component={ReportProduct} />
                        <Route path="/sso-login" component={SsoLogin} />
                        <Route path="/media-player" component={MediaPlayerRoute} />
                        <Route path="/player-report/fail" component={() => <ReportPurchaseStatus failure />} />
                        <Route path="/player-report/success" component={() => <ReportPurchaseStatus success />} />
                        <Route path="/player-report" component={ReportPurchaseReport} />
                        <LicenseRequiredRoute
                            path="/playerReport"
                            component={() => (
                                <AuthTokenWrapper>
                                    <Suspense fallback={fallback}>
                                        <PlayerReport />
                                    </Suspense>
                                </AuthTokenWrapper>
                            )}
                        />
                        <LicenseRequiredRoute
                            path="/goalieReport"
                            component={() => (
                                <AuthTokenWrapper>
                                    <Suspense fallback={fallback}>
                                        <GoalieReport />
                                    </Suspense>
                                </AuthTokenWrapper>
                            )}
                        />
                        <LicenseRequiredRoute
                            withToken
                            path="/teamNewReport"
                            component={() => (
                                <AuthTokenWrapper>
                                    <Suspense fallback={fallback}>
                                        <TeamNewReport />
                                    </Suspense>
                                </AuthTokenWrapper>
                            )}
                        />
                        <Route path="/authorize" component={AuthByToken} />
                        <PrivateRoute component={AuthorizedWrapper} />
                    </Switch>
                </ScrollToTop>
                <WaiterOfProcess />
                <LocaleLoader />
            </SettingsWrapper>
        </App>
    );
};

export default Routes;
